import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { array, bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { propTypes, LISTING_STATE_CLOSED, LINE_ITEM_NIGHT, LINE_ITEM_DAY } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import config from '../../config';
import { ModalInMobile, Button } from '../../components';
import { BookingTimeForm } from '../../forms';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './BookingPanel.css';

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const { Money } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openBookModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), book: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeBookModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'book');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    title,
    subTitle,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    currentUser,
    bonusCustomer,
    onUpdateBonusCustomer,
    onChangeUseBonus,
    useBonus,
    useBonusCheckbox,
    useBonusDisabled,
    submitButtonTitle,
    disabledFields,
    initialValues,
    timeStep,
    defaultSessionType,
    showSessionSwitch,
    additionalSession,
    isBonusesAvailable,
    isUpdateBooking,
    diffBetweenStartAndEnd,
    minutesFromDiffRange,
    isEditBookingActive,
    updateInProgress,
    children,
    allSubjects,
  } = props;

  const price = listing.attributes.price;
  const timeZone =
    listing.attributes.availabilityPlan && listing.attributes.availabilityPlan.timezone;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingTimeForm = hasListingState && !isClosed;
  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, intl);
  const isBook = !!parse(location.search).book;

  const subTitleText = !!subTitle
    ? subTitle
    : showClosedListingHelpText
    ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
    : null;

  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey = isNightly
    ? 'BookingPanel.perNight'
    : isDaily
    ? 'BookingPanel.perDay'
    : 'BookingPanel.perUnit';

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.bookingTitle);

  const userTypes = currentUser !== null ?  currentUser.attributes.profile.privateData.userType : null;
  const subjectItems = listing.attributes.publicData.subjectItems ? listing.attributes.publicData.subjectItems : [];
  const additionalItemsPrivate = listing.attributes.publicData.sessionType.indexOf('group') !== -1 ? subjectItems.map(e => {
    return { [`${e}_private`]: new Money(listing.attributes.publicData[`${e}_private`] ? listing.attributes.publicData[`${e}_private`].amount : 0,
        listing.attributes.publicData[`${e}_private`] ? listing.attributes.publicData[`${e}_private`].currency : config.currencyConfig.currency)}
  }) : null;
  // const additionalItemsPrivate = subjectItems.map(e => {
  //   return { [`${e}_private`]: new Money(listing.attributes.publicData[e] ? listing.attributes.publicData[e].amount : 0,
  //       listing.attributes.publicData[e] ? listing.attributes.publicData[e].currency : config.currencyConfig.currency)}
  // });

  const handleSubmit = values => {
    onSubmit({
      ...values,
    });
  };

  const buttonTitle = () => {
    if (!!submitButtonTitle){
      return typeof submitButtonTitle !== 'string' ? submitButtonTitle : <FormattedMessage id={submitButtonTitle} />
    }

    return <FormattedMessage id="BookingPanel.ctaButtonMessage" />;
  }

  const showOpenBookingFormButton = userTypes === 'Student' ? (
    <div className={css.openBookingForm}>
      <div className={css.priceContainer}>
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
        <div className={css.perUnit}>
          <FormattedMessage id={unitTranslationKey} />
        </div>
      </div>

      {showBookingTimeForm ? (
        <Button
          rootClassName={css.bookButton}
          onClick={() => openBookModal(isOwnListing, isClosed, history, location)}
        >
          {buttonTitle()}
        </Button>
      ) : (
        <div className={css.closedListingButton}>
          <FormattedMessage id="BookingPanel.closedListingButtonText" />
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="BookingTimeFormInModal"
        isModalOpenOnMobile={isBook || isEditBookingActive}
        onClose={() => closeBookModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalHeading}>
          <h1 className={css.title}>{title}</h1>
        </div>
        <div className={css.bookingHeading}>
          <div className={css.bookingHeadingContainer}>
            <h2 className={titleClasses}>{title}</h2>
            {subTitleText && !isUpdateBooking ? (
              <div className={css.bookingHelp}>{subTitleText}</div>
            ) : null}
          </div>
        </div>

        {showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.submitButtonWrapper}
            unitType={unitType}
            onSubmit={handleSubmit}
            price={price}
            isOwnListing={isOwnListing}
            listingId={listing.id}
            listing={listing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            currentUser={currentUser}
            additionalItems={additionalItemsPrivate}
            bonusCustomer={bonusCustomer}
            onUpdateBonusCustomer={onUpdateBonusCustomer}
            onChangeUseBonus={onChangeUseBonus}
            useBonus={useBonus}
            useBonusCheckbox={useBonusCheckbox}
            useBonusDisabled={useBonusDisabled}
            submitButtonTitle={submitButtonTitle}
            disabledFields={disabledFields}
            initialValues={initialValues}
            timeStep={timeStep}
            defaultSessionType={defaultSessionType}
            showSessionSwitch={showSessionSwitch}
            additionalSession={additionalSession}
            isBonusesAvailable={isBonusesAvailable}
            isUpdateBooking={isUpdateBooking}
            diffBetweenStartAndEnd={diffBetweenStartAndEnd}
            minutesFromDiffRange={minutesFromDiffRange}
            updateInProgress={updateInProgress}
            allSubjects={allSubjects}
          >
            {children}
          </BookingTimeForm>
        ) : null}
      </ModalInMobile>
      {showOpenBookingFormButton}
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  subTitle: null,
  unitType: config.bookingUnitType,
  monthlyTimeSlots: null,
  currentUser: null,
  additionalSession: false
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.bookingUnitType,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),
  authorDisplayName: oneOfType([node, string]).isRequired,
  onManageDisableScrolling: func.isRequired,
  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  currentPage: string,
  submitButtonTitle: oneOfType([string, node]),
  disabledFields: array,
  initialValues: object,
  timeStep: propTypes.timeStep, // for manage time step in time select components (1 hour by default)
  isUpdateBooking: bool,
  diffBetweenStartAndEnd: number,
  minutesFromDiffRange: number,
  defaultSessionType: string,
  showSessionSwitch: bool,
  additionalSession: bool,
  updateInProgress: bool,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
  isBonusesAvailable: bool,
};

export default compose(
  withRouter,
  injectIntl
)(BookingPanel);
